import bioSortingData from '@Constants/BioSortingData';
import * as Results from '@Services/Results';
import { initialize } from '@Services/DruglikenessComponent';
import defaults from 'lodash/defaults';
import each from 'lodash/each';
import toArray from 'lodash/toArray';
import assignIn from 'lodash/assignIn';
import sortBy from 'lodash/sortBy';
import isUndefined from 'lodash/isUndefined';
import find from 'lodash/find';
import * as SubstanceResource from './SubstanceResource';

class SubstanceData {
  DEFAULT_BIO_ITEMS = 7;

  bioActivityData = {
    quan: null,
    qual: null,
  };

  // eslint-disable-next-line class-methods-use-this
  retrieveBioCategories = (payload) => SubstanceResource.getBioCategories(payload);

  retrieveBioCategoryData = (payload) =>
    SubstanceResource.getBioPair(defaults(payload, { type: 'bio' }))
      .then((data) =>
        // then set the bio activity data seperated by category
        this.setBioData(payload.databaseId, data)
      )
      .catch(Logger.error);

  setBioData(databaseId, bioData) {
    const bioActivity = {};
    bioActivity.quan = bioData.quantitative && bioData.quantitative.resultSize > 0 ? bioData.quantitative : null;
    bioActivity.qual = bioData.qualitative && bioData.qualitative.resultSize > 0 ? bioData.qualitative : null;

    // for every bioactivity category retrieve its data and put it in an array
    each(bioActivity, (value, key) => {
      if (!value) {
        this.bioActivityData[key] = undefined;
      }
    });

    return new Promise((resolve, reject) => {
      Promise.all([
        this.loadBioactivityCategory(databaseId, bioActivity.quan, 'quan'),
        this.loadBioactivityCategory(databaseId, bioActivity.qual, 'qual'),
      ])
        .then(() => {
          this.bioActivityData.databaseId = databaseId;
          resolve(this.bioActivityData);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  loadBioactivityCategory(databaseId, category, key) {
    if (category) {
      return this.retrieveBioContent({
        databaseId,
        resultName: category.resultName,
        firstItem: 1,
        lastItem: this.DEFAULT_BIO_ITEMS,
        sort: bioSortingData[key].sort,
        order: bioSortingData[key].order,
      })
        .then((data) => {
          if (key === 'quan') {
            this.bioActivityData.quan = {
              data: toArray(data.entities),
              resultName: category.resultName,
              count: category.resultSize,
            };
          }
          if (key === 'qual') {
            this.bioActivityData.qual = {
              data: toArray(data.entities),
              resultName: category.resultName,
              count: category.resultSize,
            };
          }
          return key;
        })
        .catch(Logger.reject);
    }
    return null;
  }

  // eslint-disable-next-line class-methods-use-this
  retrieveBioContent = (payload) => SubstanceResource.getBioContent(payload);

  retrieveDruglikenessData(payload) {
    return SubstanceResource.getDruglikenessContent(payload)
      .then((data) => this.mapDruglikenessComponents(data, payload.databaseId))
      .catch(Logger.reject);
  }

  retrieveDruglikenessDataByXrn(payload) {
    return SubstanceResource.getDruglikenessByXrn(payload)
      .then((data) => this.mapDruglikenessComponents(data, payload.databaseId))
      .catch(Logger.reject);
  }

  getDruglikenessBySubstanceId(databaseId, substanceId) {
    return Results.getDruglikenessBySubstanceId(databaseId, substanceId)
      .then((data) => this.mapDruglikenessComponents(data, databaseId))
      .catch(Logger.reject);
  }

  // eslint-disable-next-line class-methods-use-this
  mapDruglikenessComponents = (response, databaseId = null) => initialize(response, databaseId);

  // eslint-disable-next-line class-methods-use-this
  retrieveFactData(payload, additionalPayload) {
    return SubstanceResource.getFactContent(assignIn({}, payload, additionalPayload));
  }

  // eslint-disable-next-line class-methods-use-this
  alterDataShownQuan = (passedData) => {
    const data = passedData;
    data.headers = sortBy(data.headers, 'order');
    // Go through all rows
    data.rows.forEach((passedRow) => {
      // Go through all cells per row
      const row = passedRow;

      if (!row) return;

      row.cells = sortBy(row.cells, 'order');
      row.cells.forEach((cell) => {
        // When we have cell values
        if (!isUndefined(cell.value) && cell.value.length) {
          // Find matching header and set hasContent to true
          const headerItem = find(data.headers, { id: cell.id });
          headerItem.hasContent = true;
        }
      });
    });

    // Now go over all headers
    data?.headers?.forEach((column) => {
      // if a headers column is shown
      if (column.hasContent) {
        // Go over all rows
        data?.rows?.forEach((row) => {
          // Per row go over the cells
          row?.cells?.forEach((passedCell) => {
            // When a cell.id matches a column.id set the showCell to true
            const cell = passedCell;
            if (cell && cell.id === column?.id) {
              cell.showCell = true;
            }
          });
        });
      }
    });

    return data;
  };

  // eslint-disable-next-line class-methods-use-this
  buildReportData = (headers, rows) => {
    const data = [];
    for (let i = 0; i < rows.length; i += 1) {
      data[i] = [];
      if (!rows[i].cells) {
        data[i] = rows[i];
      } else {
        for (let j = 0; j < headers.length; j += 1) {
          data[i].push({ header: headers[j], field: rows[i].cells[j] });
        }
      }
    }
    return data;
  };

  handleTargetTitles(bioData) {
    const types = ['qual', 'quan'];
    this.changeTargetTitles(bioData, types);
    return bioData;
  }

  changeTargetTitles(bioData, types) {
    if (!bioData) return;
    each(types, (type) => {
      if (bioData[type]) {
        each(bioData[type].data, this.renameTitles);
      }
    });
  }

  // eslint-disable-next-line class-methods-use-this
  renameTitles = (obj) => {
    if (obj.details.targets && obj.details.targets.length) {
      each(obj.details.targets, (element) => {
        const el = element;
        el.targets = (el.targets && el.targets?.replace(/ \+ /g, '/')) || 'No given title';
      });
    }
  };
}

export default (() => new SubstanceData())();
